import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertCircle, CheckCircle2, Cpu, Loader2 } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import { apiClient } from "@/services/api";
import { useToast } from "@/hooks/useToast";

export default function LinkDevicePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation(["auth", "common"]);
  const toast = useToast();
  const [isApproving, setIsApproving] = useState(false);
  const [approvedDeviceId, setApprovedDeviceId] = useState<string | null>(null);

  const code = searchParams.get("code")?.trim() ?? "";
  const isValidCode =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      code,
    );

  const approve = async () => {
    if (!isValidCode) return;
    setIsApproving(true);
    try {
      const result = await apiClient.approveDeviceLinkChallenge(code);
      setApprovedDeviceId(result.deviceId);
      toast.success(t("auth:linkDeviceApprovedToast"));
    } catch {
      toast.error(t("auth:linkDeviceApproveError"));
    } finally {
      setIsApproving(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-50 flex flex-col">
      <Navbar hideSidebarButtons={true} />

      <main className="flex-1 px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto flex w-full max-w-lg items-center justify-center">
          <section className="w-full rounded-2xl border border-secondary-200 bg-white p-6 shadow-[0_20px_70px_rgba(17,24,39,0.08)] sm:p-8">
            <div className="mb-6 flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-secondary-200 bg-secondary-50 text-primary-600">
                {approvedDeviceId ? (
                  <CheckCircle2 className="h-6 w-6" />
                ) : (
                  <Cpu className="h-6 w-6" />
                )}
              </div>
              <div>
                <p className="font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-secondary-400">
                  Trackcast
                </p>
                <h1 className="mt-2 text-2xl font-bold text-secondary-900">
                  {t("auth:linkDeviceApproveTitle")}
                </h1>
                <p className="mt-2 text-sm leading-6 text-secondary-600">
                  {approvedDeviceId
                    ? t("auth:linkDeviceApprovedDescription")
                    : t("auth:linkDeviceApproveDescription")}
                </p>
              </div>
            </div>

            {!isValidCode && (
              <div className="mb-6 flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <p>{t("auth:linkDeviceInvalidCode")}</p>
              </div>
            )}

            {isValidCode && !approvedDeviceId && (
              <div className="mb-6 rounded-lg border border-secondary-200 bg-secondary-50 p-4">
                <p className="text-xs font-medium uppercase tracking-wide text-secondary-500">
                  {t("auth:linkDeviceChallengeCode")}
                </p>
                <p className="mt-2 break-all font-mono text-sm text-secondary-800">
                  {code}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-3 sm:flex-row">
              {approvedDeviceId ? (
                <button
                  type="button"
                  className="btn-primary w-full px-4 py-2.5"
                  onClick={() => navigate("/my-page?tab=myDevices")}
                >
                  {t("auth:goToDevices")}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-primary w-full px-4 py-2.5"
                  disabled={!isValidCode || isApproving}
                  onClick={approve}
                >
                  {isApproving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("common:loading")}
                    </>
                  ) : (
                    t("auth:approveDeviceLink")
                  )}
                </button>
              )}
              <button
                type="button"
                className="btn-secondary w-full px-4 py-2.5"
                onClick={() => navigate("/")}
              >
                {t("common:cancel")}
              </button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
